import { ChangeDetectionStrategy, Component, forwardRef, inject, input, model, output, viewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DialogModule } from '@openng/optimus-ui/dialog';
import {
  FormComponent,
  PROJECTED_FORM_HOST,
  ProjectedFormGroupDirective,
  ProjectedFormHost,
  projectedFormProxy,
} from '@shared/ui/forms';
import { DialogFooterComponent } from '../dialog-footer/dialog-footer.component';

/**
 * Formulářový dialog: modální p-dialog s hlavičkou, textem (HTML přes `innerHTML`), formulářem (`ui-form`)
 * s promítnutými poli, patičkou Potvrdit/Zrušit (`ui-dialog-footer`) a resetem formuláře po zavření.
 *
 * Pole (`formControlName`) deklaruje obalující dialog ve své šabloně jako obsah komponenty; formulář (vstup `form`)
 * a stav odeslání pro zobrazení chyb jim zprostředkuje hostitel (`ProjectedFormGroupDirective`,
 * `projectedFormProxy`, viz libs/shared/ui/forms/src/lib/form/projected-form.ts). Obalující dialog drží vlastní
 * `FormGroup`, validaci, tvar emitovaných dat a zavření po úspěšném odeslání.
 *
 * @example
 * <ui-form-dialog
 *   [(visible)]="dialogVisible"
 *   [form]="form"
 *   header="Potvrzení akce"
 *   text="Chcete provést akci?"
 *   submitLabel="Potvrdit"
 *   cyPrefix="duvod-dialog"
 *   (handleSubmit)="confirmAction()">
 *   <ui-textarea formControlName="duvod" label="Důvod/komentář" />
 * </ui-form-dialog>
 */
@Component({
  selector: 'ui-form-dialog',
  imports: [FormComponent, DialogModule, DialogFooterComponent],
  template: `
    <p-dialog
      appendTo="self"
      [header]="header()"
      [modal]="true"
      [style]="dialogStyle()"
      [(visible)]="visible"
      (onHide)="form.reset()">
      <span [class]="textClass()" [innerHTML]="text()"></span>
      <ui-form (handleSubmit)="handleSubmit.emit()">
        <ng-content />
        <ui-dialog-footer
          [class]="footerClass()"
          [submitLabel]="submitLabel()"
          [cyPrefix]="cyPrefix()"
          [submitCy]="submitCy()"
          [cancelCy]="cancelCy()"
          (handleCancel)="visible.set(false)" />
      </ui-form>
    </p-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [{ directive: ProjectedFormGroupDirective, inputs: ['formGroup: form'] }],
  providers: [
    { provide: PROJECTED_FORM_HOST, useExisting: forwardRef(() => FormDialogComponent) },
    { provide: FormComponent, useFactory: projectedFormProxy },
  ],
})
export class FormDialogComponent implements ProjectedFormHost {
  private readonly formGroupDirective = inject(ProjectedFormGroupDirective, { self: true });

  readonly visible = model(false);

  readonly header = input<string>();
  /** Text nad formulářem; může obsahovat HTML. */
  readonly text = input<string>();
  readonly textClass = input('block mb-8 text max-w-160');
  /** Inline styl p-dialog (např. pevná šířka). */
  readonly dialogStyle = input<Record<string, string>>();

  readonly submitLabel = input.required<string>();
  /** Předpona `data-cy` tlačítek patičky (`<prefix>-submit`, `<prefix>-zrusit`), viz `ui-dialog-footer`. */
  readonly cyPrefix = input<string>();
  readonly submitCy = input<string>();
  readonly cancelCy = input<string>();
  readonly footerClass = input<string>();

  /** Odeslání formuláře (tlačítko potvrzení, Enter v poli). */
  readonly handleSubmit = output<void>();

  /** Vnitřní `ui-form` – stav odeslání pro promítnutá pole (`projectedFormProxy`). */
  readonly formComponent = viewChild(FormComponent);

  /** Formulář dialogu (vstup `form` – binding host direktivy `ProjectedFormGroupDirective`). */
  get form(): FormGroup {
    return this.formGroupDirective.form;
  }
}
